import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { MoveArrow, type RevisionBaseline } from './DiffBits'
import { type PlanNodeFromServer } from './VersionViewOverallSection'
import { formatChange, headline, revisionMove } from './revisionMove'

// The headline over a diff: how much a revision moves the plan's total and
// which top-level rows moved the most, or that this is the plan's first
// version. Shared by the review page (reviewer) and the submit page (doer);
// `legend` tells the reader where the green/red marks are and shows only when
// something changed; `instruction` is a one-line how-to on top, for the
// reviewer.
export default function RevisionBanner({ baseline, planNodes, currency, legend, instruction, className }: {
  baseline: RevisionBaseline
  planNodes: PlanNodeFromServer[]
  currency: string | null
  legend: string
  instruction?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn('flex flex-col gap-1 rounded-lg border px-4 py-2.5 text-sm text-foreground text-center', className)}
      style={{ background: 'var(--info-surface)', borderColor: 'var(--border)' }}
    >
      {instruction && <p className="mb-1 border-b pb-2" style={{ borderColor: 'var(--border)' }}>{instruction}</p>}
      <Summary baseline={baseline} planNodes={planNodes} currency={currency} />
      {baseline.summary.any_changes && legend && <p>{legend}</p>}
    </div>
  )
}

function Summary({ baseline, planNodes, currency }: {
  baseline: RevisionBaseline
  planNodes: PlanNodeFromServer[]
  currency: string | null
}) {
  if (baseline.first_publish) {
    return <p><span className="font-semibold">First version of this plan.</span> Nothing is published yet, so every row is new.</p>
  }
  if (!baseline.summary.any_changes) {
    return <p className="font-semibold">No changes from the published plan.</p>
  }

  const move = revisionMove(baseline, planNodes)
  return (
    <>
      <p className="font-semibold">{headline(move, currency)}.</p>
      {move.moves.length > 0 && (
        <p>
          Biggest moves:{' '}
          {move.moves.map((m, i) => {
            return (
              <span key={m.name} className="whitespace-nowrap">
                {i > 0 && ' · '}
                <MoveArrow up={m.now > m.was} />
                {m.name} {formatChange(m.was, m.now, currency)}
              </span>
            )
          })}
        </p>
      )}
    </>
  )
}
